/**
 * A mark for each thing a unit can be on the market as.
 *
 * The same job the property-type marks do: make a list scannable without reading it. Somebody
 * looking for what is for sale should find it by shape and colour before they find it by word.
 *
 * **Distinct at a glance, not just distinct on inspection.** Five marks that are all a small house
 * with a different squiggle are five identical marks at 16px. Each of these is a different overall
 * silhouette — a key, a document, a tag, a moon, a handshake — so the difference survives being
 * shrunk into a table row.
 *
 * **One colour.** The property-type marks are two-toned, and that split works there because the
 * tiles are grouped under headings that say what the colours mean. These five sit in a single row
 * with nothing to explain a split, so a second colour would read as decoration applied at random —
 * which is exactly how the ungrouped type tiles read before they were grouped.
 *
 * Hand-drawn on the same 24 grid at 1.75 stroke as `icons.tsx`, and deliberately free of any
 * dependency: the listing app will want these too, and a shared package is a better home for them
 * than a copy — see the note in the handoff.
 */

import type { ReactElement, SVGProps } from "react";
import { OFFER_TYPES } from "@rentorng/core";

type MarkProps = SVGProps<SVGSVGElement> & { size?: number };

function Mark({ size = 18, children, ...rest }: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** To rent — a key. The thing that changes hands is the use of the place, not the place. */
const IconRent = (props: MarkProps) => (
  <Mark {...props}>
    <circle cx="8" cy="9" r="4" />
    <path d="M11 11.5 20 20.5M17.5 18l2-2M14.5 15l2.5-2.5" />
  </Mark>
);

/** To lease — a signed document. A lease is the agreement more than it is the key. */
const IconLease = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M6 3.5h8l4 4v13H6z" />
    <path d="M14 3.5v4h4" />
    <path d="M9 16.5c1.5-2.5 2.5 1 4-1.5" />
  </Mark>
);

/** For sale — a price tag. The building itself is what is on offer. */
const IconSale = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M11.5 3.5H20v8.5l-8.8 8.8a1.6 1.6 0 0 1-2.3 0l-6.2-6.2a1.6 1.6 0 0 1 0-2.3z" />
    <circle cx="16.4" cy="7.6" r="1.5" />
  </Mark>
);

/** Short let — a moon. Counted in nights rather than in years. */
const IconShortLet = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
  </Mark>
);

/** Joint venture — two hands meeting. Nobody is buying; both sides are putting something in. */
const IconJointVenture = (props: MarkProps) => (
  <Mark {...props}>
    <path d="M2.5 10.5 6 7l3.5 3.5L12 13l-2 2-3-3" />
    <path d="M21.5 13.5 18 17l-3.5-3.5" />
    <path d="M9.5 10.5 12 8l2.5 2.5 3 3" />
  </Mark>
);

const MARKS: Record<string, (props: MarkProps) => ReactElement> = {
  rent: IconRent,
  lease: IconLease,
  sale: IconSale,
  short_let: IconShortLet,
  joint_venture: IconJointVenture,
};

export function OfferIcon({ offer, size = 18, className = "" }: { offer: string; size?: number; className?: string }) {
  const Component = MARKS[offer];
  if (!Component) return null;
  return <Component size={size} className={className} />;
}

/**
 * Choosing what a unit is on the market as.
 *
 * A tile rather than a chip: the mark sits above the word and is drawn large enough to be
 * recognised on its own, which is the whole point of having marks. At chip size it was an
 * ornament to the left of the label and did no work.
 *
 * Both forms on the unit screen use this — the single one and the bulk run — because two hand-
 * rolled copies of the same picker is how they drift apart, and the second one is the one that
 * quietly loses a new offer type.
 *
 * **Never empty.** Untickng the last one is refused rather than allowed and then defaulted on the
 * server: a unit that is on the market as nothing is not a state anybody means.
 */
export function OfferPicker({
  value,
  onChange,
  columns = 3,
}: {
  value: readonly string[];
  onChange: (next: string[]) => void;
  /** Tiles across, from the `sm` breakpoint up. Below it, always two. */
  columns?: 3 | 5;
}) {
  const toggle = (offer: string) => {
    if (!value.includes(offer)) {
      onChange([...value, offer]);
      return;
    }
    const next = value.filter((entry) => entry !== offer);
    if (next.length) onChange(next);
  };

  return (
    <div className={`grid grid-cols-2 gap-2 ${columns === 5 ? "sm:grid-cols-5" : "sm:grid-cols-3"}`}>
      {OFFER_TYPES.map((entry) => {
        const chosen = value.includes(entry.value);
        return (
          <button
            key={entry.value}
            type="button"
            aria-pressed={chosen}
            onClick={() => toggle(entry.value)}
            className={`flex min-h-[5rem] flex-col items-center justify-center gap-1.5 rounded-[var(--radius-control)] border px-2 py-3 text-center text-[13px] leading-tight transition-colors ${
              chosen
                ? "border-brand-500 bg-brand-50 font-semibold text-brand-800"
                : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50"
            }`}
          >
            <OfferIcon offer={entry.value} size={26} className={chosen ? "text-brand-600" : "text-ink-400"} />
            <span className="w-full truncate">{entry.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The mark with its word, as a chip.
 *
 * Used in lists and on a unit's own page, and the shape a listing card will want: the colour and
 * the mark carry it at a glance, the word settles it.
 */
export function OfferTag({ offer, className = "" }: { offer: string; className?: string }) {
  const info = OFFER_TYPES.find((entry) => entry.value === offer);
  if (!info) return null;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[var(--radius-control)] bg-brand-50 px-2 py-1 text-xs font-medium text-brand-800 ${className}`}
    >
      <OfferIcon offer={offer} size={15} />
      {info.short}
    </span>
  );
}

/**
 * Every offer a unit carries, in one row.
 *
 * Ordered by `OFFER_TYPES` rather than by how they were ticked, so the same pair of offers looks
 * the same on every row of a list.
 */
export function OfferTags({ offers, className = "" }: { offers: readonly string[] | null; className?: string }) {
  const chosen = new Set(offers ?? []);
  const shown = OFFER_TYPES.filter((offer) => chosen.has(offer.value));
  if (!shown.length) return null;

  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {shown.map((offer) => (
        <OfferTag key={offer.value} offer={offer.value} />
      ))}
    </span>
  );
}
